import {
  autoUpdate,
  flip,
  hide,
  limitShift,
  offset,
  type Placement,
  shift,
  size,
  useFloating,
} from '@floating-ui/vue';
import type { Ref } from 'vue';
import { computed, type MaybeRef, toValue } from 'vue';

import { type MaybeElement, remSize, unwrapElement } from '@/kasten-components/utils';

type ElementReference = Readonly<Ref<MaybeElement<HTMLElement>>>;

export type Settings = {
  // Disable floating updates. Useful when floating is hidden with css.
  opened?: MaybeRef<boolean>;
  // Gap between dropdown and trigger button in rem
  offset: number;
  // In rem
  dropdownRect?: {
    maxHeight?: number;
    minHeight?: number;
    maxWidth?: number;
    minWidth?: number;
  };
  scrollContainer?: ElementReference;
  defaultPlacement?: MaybeRef<Placement>;
};

export const useFloatingDropdown = (
  reference: ElementReference,
  floating: ElementReference,
  settings: Settings,
) => {
  const open = computed(() => toValue(settings.opened) ?? true);
  const optionsContainerBoundaries = computed(() => ({
    maxHeight: (settings.dropdownRect?.maxHeight ?? 50) * remSize,
    minHeight: (settings.dropdownRect?.minHeight ?? 10) * remSize,
    maxWidth: (settings.dropdownRect?.maxWidth ?? 15) * remSize,
    minWidth: (settings.dropdownRect?.minWidth ?? 10) * remSize,
  }));
  const offsetFromSettings = settings.offset * remSize;

  const instance = useFloating(
    reference,
    computed(() => (open.value ? unwrapElement(floating) : undefined)),
    {
      open,
      transform: true,
      whileElementsMounted: (...args) => autoUpdate(...args, { animationFrame: true }),
      placement: settings.defaultPlacement ?? 'bottom',
      middleware: [
        offset(offsetFromSettings),
        shift({
          padding: offsetFromSettings,
          limiter: limitShift({ offset: (state) => 0.3 * state.rects.floating.width }),
        }),
        size((state) => ({
          apply({ availableWidth, availableHeight, elements }) {
            const optionsList =
              settings.scrollContainer !== undefined
                ? unwrapElement(settings.scrollContainer)
                : elements.floating;
            if (optionsList) {
              Object.assign(optionsList.style, {
                maxWidth:
                  Math.max(
                    Math.min(availableWidth, optionsContainerBoundaries.value.maxWidth),
                    optionsContainerBoundaries.value.minWidth,
                  ) + 'px',
                maxHeight:
                  Math.max(
                    Math.min(availableHeight, optionsContainerBoundaries.value.maxHeight),
                    optionsContainerBoundaries.value.minHeight,
                  ) + 'px',
                minWidth: state.rects.reference.width + 'px',
              });
            }
          },
          padding: offsetFromSettings,
        })),
        flip(),
        hide((state) => ({
          padding: {
            left: 0.4 * state.rects.reference.width,
            right: 0.4 * state.rects.reference.width,
          },
        })),
      ],
    },
  );

  const isReferenceOutsideOfView = computed(
    () => instance.middlewareData.value?.hide?.referenceHidden === true,
  );

  return { ...instance, isReferenceOutsideOfView };
};
